import { Flex, Grid } from 'antd';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import MessageDisplay from './Messages/Messages';
import ConversationsList from './Conversations/Conversations';
import useConversationSocketEvents from './hooks/useConversationSocketEvents';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import { Resource } from '../../Constants/socket';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import useChannelSelection from './hooks/useChannelSelection';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import '../Tickets/Tickets.css';

const { useBreakpoint } = Grid;

const MIN_CONVERSATIONS_WIDTH = 280;
const MIN_MESSAGES_WIDTH = 400;

const Index = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [showMessages, setShowMessages] = useState(false);
    const screens = useBreakpoint();
    const location = useLocation();
    const { user: { id: currentUserId } = {} } = useGlobal();
    const { handleError } = useErrorHandler();
    const { isDarkMode } = useTheme();

    // Resizable divider state
    const [conversationsWidth, setConversationsWidth] = useState(null);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef(null);

    const {
        channels,
        loading: loadingChannels,
        selectedChannel,
        setSelectedChannel,
        handleSearch: onChannelSearch
    } = useChannelSelection(handleError);

    // Extract contactId from URL query parameter
    const initialContactId = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('contactId');
    }, [location.search]);

    // Subscribe to user-scoped channel instead of global resources
    useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);

    // Use the conversation socket events hook to handle real-time updates
    useConversationSocketEvents({
        setConversations,
        selectedConversation,
        setSelectedConversation,
        selectedChannel
    });



    useEffect(() => {
        if (!screens.xs) {
            setShowMessages(false);
        }
    }, [screens.xs]);

    const handleConversationSelect = useCallback((conversation) => {
        setSelectedConversation(conversation);
        if (screens.xs) {
            const willShow = Boolean(conversation);
            setShowMessages(willShow);
            // Push a history entry every time we enter the messages view on mobile
            if (willShow) {
                try { window.history.pushState({ messageView: true }, ''); } catch (e) { }
            }
        }
    }, [screens.xs, setSelectedConversation, setShowMessages]);

    const handleBack = useCallback(() => {
        setShowMessages(false);
    }, [setShowMessages]);

    // Centralized mobile back button behavior: works every time we open the message view
    useEffect(() => {
        if (!screens.xs) return;

        const handlePopState = (event) => {
            // If we are showing messages, back should go to conversations
            if (showMessages) {
                event?.preventDefault?.();
                setShowMessages(false);
                return;
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [screens.xs, showMessages]);

    // Resizable divider handlers
    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            if (!containerRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();
            const newWidth = e.clientX - containerRect.left;
            const maxWidth = containerRect.width - MIN_MESSAGES_WIDTH;
            setConversationsWidth(Math.max(MIN_CONVERSATIONS_WIDTH, Math.min(newWidth, maxWidth)));
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const isMobile = Boolean(screens.xs);

    return (
        <Flex
            ref={containerRef}
            style={{
                flexGrow: 1,
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
                borderRadius: 8,
                height: '100%',
                minHeight: 0,
                userSelect: isResizing ? 'none' : undefined,
                cursor: isResizing ? 'col-resize' : undefined,
            }}
        >
            <div
                style={{
                    display: isMobile && showMessages ? 'none' : 'flex',
                    width: isMobile ? '100%' : (conversationsWidth || undefined),
                    flex: isMobile ? '1 0 100%' : (conversationsWidth ? `0 0 ${conversationsWidth}px` : '0 0 33.333%'),
                    overflow: 'hidden',
                    minHeight: 0,
                }}
            >
                <ConversationsList
                    onSelectConversation={handleConversationSelect}
                    selectedId={selectedConversation?.id}
                    conversations={conversations}
                    setConversations={setConversations}
                    isMobile={isMobile}
                    initialContactId={initialContactId}
                    channels={channels}
                    loadingChannels={loadingChannels}
                    selectedChannel={selectedChannel}
                    setSelectedChannel={setSelectedChannel}
                    onChannelSearch={onChannelSearch}
                />
            </div>

            {/* Resizable divider — desktop only */}
            {!isMobile && (
                <div
                    className={`ticket-detail-divider${isDarkMode ? ' dark' : ''}${isResizing ? ' active' : ''}`}
                    onMouseDown={handleResizeStart}
                />
            )}

            <div
                style={{
                    display: isMobile && !showMessages ? 'none' : 'flex',
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    minWidth: 0,
                    minHeight: 0,
                }}
            >
                <MessageDisplay
                    selectedConversation={selectedConversation}
                    onBack={handleBack}
                    setConversations={setConversations}
                    setSelectedConversation={setSelectedConversation}
                    screens={screens}
                />
            </div>
        </Flex>
    );
};

export default Index;